import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FacebookIcon from '@mui/icons-material/Facebook';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InstagramIcon from '@mui/icons-material/Instagram';
import MailIcon from '@mui/icons-material/Mail';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { createTheme, styled, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Config } from 'src/config';
import { useDesktop } from 'src/contexts/DesktopProvider';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import { NavConfig } from 'src/navConfig';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import { useToaster } from 'src/utils/toaster/toasterContext';
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';
import TrialPlanDetailsPopup from 'src/trailPlanDetailsPopup';
import FeedbackPopup from 'src/components/FeedbackPopup';
import React from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import useMediaQuery from '@mui/material/useMediaQuery';
import RenewPlanWarning from 'src/components/RenewPlanWarning';
import PreviousPaidPlanWarning from 'src/components/PreviousPaidPlanWarning';
import { LoadLanguage } from 'src/components/languageService';

const inactiveProfileServiceConfig = {
  whatsapp: {
    label: 'WhatsApp',
    color: '#25D366',
    Icon: WhatsAppIcon,
    nameKeys: ['display_name', 'phone_number'],
    detailKeys: ['phone_number', 'waba_id', 'profile_id'],
  },
  facebook: {
    label: 'Facebook',
    color: '#1877F2',
    Icon: FacebookIcon,
    nameKeys: ['display_name'],
    detailKeys: ['category', 'profile_id'],
  },
  instagram: {
    label: 'Instagram',
    color: '#E1306C',
    Icon: InstagramIcon,
    nameKeys: ['display_name'],
    detailKeys: ['category', 'business_account_id', 'profile_id'],
  },
};

const openMetaOAuth = (url, popupName) => {
  if (CommonHelper.CustomDomain()) {
    const popup = window.open(url, popupName, 'width=720,height=760');
    if (popup) {
      popup.focus();
      return;
    }
  }
  window.location.href = url;
};

const startHeaderMetaOAuth = ({ service, profileID = '' }) => {
  const isInstagram = service === 'instagram';
  const metaConfig = isInstagram ? Config.Instagram : Config.Facebook;
  const redirectPath = isInstagram ? '/instagram-redirect' : '/facebook-redirect';
  const storagePrefix = isInstagram ? 'instagram' : 'facebook';
  const redirectURL = CommonHelper.GetMetaOAuthRedirectUri(redirectPath);

  window.localStorage.setItem(`${storagePrefix}_oauth_redirect_uri`, redirectURL);
  window.localStorage.setItem(`${storagePrefix}_oauth_mode`, 'reauth');
  window.localStorage.setItem(isInstagram ? 'instagram_oauth_instagram_id' : 'facebook_oauth_page_id', profileID);
  window.localStorage.removeItem(isInstagram ? 'instagram_oauth_account_id' : 'facebook_oauth_account_id');
  if (isInstagram) {
    window.localStorage.removeItem('facebook_oauth_page_id');
  } else {
    window.sessionStorage.removeItem('facebook_integration_pages');
  }

  const statePayload = isInstagram
    ? { service: 'instagram', mode: 'reauth', instagram_id: profileID }
    : { service: 'facebook', mode: 'reauth', page_id: profileID };
  const params = new URLSearchParams({
    client_id: String(metaConfig.appId),
    redirect_uri: redirectURL,
    scope: metaConfig.scope,
    response_type: 'code',
    auth_type: 'reauthorize',
    state: CommonHelper.BuildMetaOAuthState(statePayload),
  });

  openMetaOAuth(`${isInstagram ? metaConfig.authorizeUrl : `https://www.facebook.com/${metaConfig.version}/dialog/oauth`}?${params.toString()}`, `${service}-oauth`);
};

export default function Header({ headerName }) {
  const selectedLanguage = localStorage.getItem('i18nextLng') || 'english';
  const { t: translate } = useTranslation();
  const { isCollapse } = useCollapseDrawer();
  const isLargeScreen = useMediaQuery('(min-width:1550px)');

  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useDesktop();
  const theme = useTheme();
  const { showToast } = useToaster();
  const reAuthContext = useReAuth();
  const inactiveProfiles = reAuthContext?.inactiveProfiles || [];
  const inactiveProfilesLoading = reAuthContext?.inactiveProfilesLoading || false;
  const refreshInactiveProfiles = reAuthContext?.refreshInactiveProfiles;
  const clearInactiveProfile = reAuthContext?.clearInactiveProfile;

  const isOrgUser = CommonHelper.GetBrandInfoFromLocalStorage('is_org_user');
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage('is_portal_admin');
  const userOrgDetails = CommonHelper.GetBrandInfoFromLocalStorage();
  const currentPlanInfo = CommonHelper.GetBrandInfoFromLocalStorage('current_plan_info');
  const [showRenewWarning, setShowRenewWarning] = useState(CommonHelper.CheckIsRenewPlanWarning(currentPlanInfo));
  const [showPreviousPaidPlanWarning, setShowPreviousPaidPlanWarning] = useState(
    CommonHelper.CheckIsPreviousPaidPlanWarning(currentPlanInfo)
  );
  const freePlan = 'free_plan';
  const previousPaidPlan = currentPlanInfo?.previous_paid_plan_details;
  const currentBrandID = CommonHelper.GetCurrentBrandId();

  // State variable
  const [isVerified, setisVerified] = useState(true);
  const [isPlanExpired, setPlanExpired] = useState(false);
  const [showPlanDetailsPopup, setShowPlanDetailsPopup] = useState(false);
  const [isFeedbackPopupOpen, setIsFeedbackPopupOpen] = useState(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [language, setLanguage] = useState(selectedLanguage);
  const [inactiveDialogOpen, setInactiveDialogOpen] = useState(false);

  useEffect(() => {
    getUserInfo();
   

    // window.Brand2Chat_API = window.Brand2Chat_API || {};
    // window.Brand2Chat_API.onLoad = function () {
    //   if (typeof window.Brand2Chat_API.hideWidget === 'function') {
    //     window.Brand2Chat_API.hideWidget();
    //   }
    // };
  }, []);

  useEffect(() => {
    if (!currentBrandID || typeof refreshInactiveProfiles !== 'function') return;
    refreshInactiveProfiles();
  }, [currentBrandID, refreshInactiveProfiles]);

  const completeHeaderMetaOAuth = useCallback((payload) => {
    if (!payload?.service || location.pathname.startsWith('/integrations')) return;
    if (payload.error) {
      showToast(payload.error, 'e');
      return;
    }
    const service = payload.service;
    const code = payload.code || '';
    if (!code || (service !== 'facebook' && service !== 'instagram')) return;
    if (CommonHelper.MarkMetaOAuthCodeHandled(service, code)) return;

    const isInstagram = service === 'instagram';
    const profileID = isInstagram ? payload.instagram_id || payload.account_id : payload.page_id || payload.account_id;
    if (!profileID) {
      showToast(`Missing ${service} profile id for re-authentication.`, 'e');
      return;
    }

    const storagePrefix = isInstagram ? 'instagram' : 'facebook';
    const profileStorageKey = isInstagram ? 'instagram_oauth_instagram_id' : 'facebook_oauth_page_id';
    const redirectURL =
      payload.redirect_url ||
      window.localStorage.getItem(`${storagePrefix}_oauth_redirect_uri`) ||
      CommonHelper.GetMetaOAuthRedirectUri(isInstagram ? '/instagram-redirect' : '/facebook-redirect');
    const clearOAuthStorage = () => {
      window.localStorage.removeItem(`${storagePrefix}_oauth_redirect_uri`);
      window.localStorage.removeItem(`${storagePrefix}_oauth_mode`);
      window.localStorage.removeItem(profileStorageKey);
      window.localStorage.removeItem(`${storagePrefix}_oauth_account_id`);
      window.localStorage.removeItem('instagram_oauth_account_id');
      window.localStorage.removeItem('facebook_oauth_page_id');
    };

    HttpServices.Post({
      url: isInstagram ? userModuleURL.instagramReauthenticate : userModuleURL.facebookReauthenticate,
      body: isInstagram
        ? { code, instagram_id: profileID, redirect_url: redirectURL }
        : { code, page_id: profileID, redirect_url: redirectURL },
      successCallback: (_data, message) => {
        clearOAuthStorage();
        clearInactiveProfile?.(service, profileID);
        refreshInactiveProfiles?.(true);
        showToast(message || `${isInstagram ? 'Instagram account' : 'Facebook page'} re-authenticated successfully.`, 's');
        setInactiveDialogOpen(false);
      },
      failureCallback: (message) => {
        clearOAuthStorage();
        refreshInactiveProfiles?.(true);
        showToast(message || `${isInstagram ? 'Instagram account' : 'Facebook page'} re-authentication failed.`, 'e');
      },
    });
  }, [clearInactiveProfile, location.pathname, refreshInactiveProfiles, showToast]);

  useEffect(() => {
    const onMetaOAuthMessage = (event) => {
      if (!CommonHelper.IsTrustedMetaOAuthMessageOrigin(event.origin)) return;
      const payload = event.data || {};
      if (payload?.type !== 'brand2chat:meta-oauth-code') return;
      completeHeaderMetaOAuth(payload);
    };
    window.addEventListener('message', onMetaOAuthMessage);
    return () => window.removeEventListener('message', onMetaOAuthMessage);
  }, [completeHeaderMetaOAuth]);
  const getUserInfo = () => {
    let isVerified = CommonHelper.GetUserIsVerified();
    let planDetails = CommonHelper.GetCurrentFromLocalStorage();
    setisVerified(isVerified);
    if (planDetails) {
      if (planDetails.plan_id === freePlan || planDetails.expire_in_days === 0) setPlanExpired(true);
    }
  };
  const navigateToPayment = () => {
    navigate('/billing');
  };
  const failureCallback = (message) => {
    message && showToast(message, 'e');
  };
  const successCallback = (data, message) => {
    message && showToast(message, 's');
  };
  const resendEmail = () => {
    let postProps = {
      url: userModuleURL.resendVerificationMail,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleInactiveProfileReauthenticate = (profile) => {
    setInactiveDialogOpen(false);
    if (profile.service === 'whatsapp') {
      navigate('/integrations', { state: { social_reauth_service: 'whatsapp', profile_id: profile.profileID } });
      return;
    }
    startHeaderMetaOAuth({ service: profile.service, profileID: profile.profileID });
  };

  const handleOpenSettings = () => {
    navigate('/profile');
  };

  // const handleOpenChatBot = () => {
  //   const brand2ChatApi = window.Brand2Chat_API;
  //   const tawkApi = window.Tawk_API;
  //   const chatApi = typeof brand2ChatApi?.isChatHidden === 'function' ? brand2ChatApi : tawkApi;
  //   if (chatApi && typeof chatApi.isChatHidden === 'function') {
  //     if (chatApi.isChatHidden()) {
  //       chatApi.showWidget();
  //       if (typeof chatApi.open === 'function') {
  //         chatApi.open();
  //       } else if (typeof chatApi.maximize === 'function') {
  //         chatApi.maximize();
  //       }
  //     } else if (typeof chatApi.hideWidget === 'function') {
  //       chatApi.hideWidget();
  //     }
  //   } else {
  //     showToast('Chat widget is not available right now', 'e');
  //   }
  // };

  const closePopup = () => {
    setShowPlanDetailsPopup(false);
  };

  const handleOpenFeedbackPopup = () => {
    setIsFeedbackPopupOpen(true);
  };
  const handleCloseFeedbackPopup = () => {
    setIsFeedbackPopupOpen(false);
  };
  const feedbackSuccessCallback = () => {
    showToast('Thank you for your feedback!', 's');
    setIsFeedbackLoading(false);
    setIsFeedbackPopupOpen(false);
  };
  const feedbackFailureCallback = (message) => {
    showToast(message, 'e');
    setIsFeedbackLoading(false);
  };
  const handleFeedbackSubmit = ({ rating, feedback }) => {
    setIsFeedbackLoading(true);
    let postProps = {
      url: integrateModuleURL.handleContactUs,
      successCallback: feedbackSuccessCallback,
      failureCallback: feedbackFailureCallback,
      body: {
        subject: 'Write a review',
        message: `Rating - ${rating}. Feedback - ${feedback}. Orgid - ${userOrgDetails.org_id}`,
      },
    };
    HttpServices.Post(postProps);
  };

  const isShowStartTrialButton = () => {
    if (
      isOrgUser &&
      isPortalAdmin &&
      currentPlanInfo?.has_subscription &&
      currentPlanInfo?.has_trail_plan &&
      currentPlanInfo?.has_trial_period &&
      !currentPlanInfo.is_appsumo_user
    ) {
      return true;
    } else {
      return false;
    }
  };

  //Styled Components
  const HEADER_MOBILE = 64;

  const customTheme = createTheme({
    breakpoints: {
      values: {
        mdCustom: 1100,
        mdCustom0: 1250,
      },
    },
  });

  const StyledRoot = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    boxShadow: 'none',
    [customTheme.breakpoints.up('mdCustom')]: {
      width: '100%',
      ...(headerName !== '' && {
        width: `calc(100% - ${NavConfig.NAVBAR.DASHBOARD_WIDTH + 70}px)`,
        ...(isCollapse && {
          width: `calc(100% - ${NavConfig.NAVBAR.DASHBOARD_COLLAPSE_WIDTH + 72}px)`,
        }),
      }),
      height: '64px',
      zIndex: '1101',
      backdropFilter: 'blur(6px)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      transition: 'height 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      borderBottom: '1px solid ' + theme.palette.primary.main,
      borderBottomStyle: 'dashed',
    },
  }));
  const handleHelperTextClick = () => {
    navigate('/manage-brand');
  };
  const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    minHeight: HEADER_MOBILE,
    [customTheme.breakpoints.up('mdCustom')]: {
      padding: theme.spacing(0, 2.5),
    },
  }));

  const handleLanguageChange = async (event) => {
    const lang = event.target.value;
    setLanguage(lang);
    localStorage.setItem('i18nextLng', lang);

    try {
      await LoadLanguage(lang);
      window.location.reload();
    } catch (error) {
      showToast('Failed to load language', 'e');
    }
  };

  return (
    <>
      <StyledRoot>
        <StyledToolbar>
          <Typography
            sx={{
              color: 'black',
              fontWeight: 700,
              fontSize: '20px',
            }}
          >
            {headerName}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack
            direction="row"
            alignItems="center"
            spacing={{
              xs: 0.5,
              sm: 1,
            }}
          >
            {headerName && (
              <>
                {isPlanExpired && (
                  <Button
                    size="small"
                    sx={{ background: theme.palette.primary.main }}
                    variant="contained"
                    onClick={navigateToPayment}
                  >
                    <Badge variant="dot" sx={{ mr: 1 }}>
                      <MailIcon />
                    </Badge>
                    {translate('integration.upgrade_to_pro')}
                  </Button>
                )}
               
                {!isVerified && isDesktop && (
                  <Tooltip title={!isLargeScreen ? translate('header.verify_email') : ''} arrow placement="bottom">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#ffebee',
                        p: 1,
                        px: isLargeScreen ? 2 : 1,
                        borderRadius: 2,
                        gap: 1,
                      }}
                    >
                      <MailIcon color="error" sx={{ fontSize: 20 }} />
                      {isLargeScreen && (
                        <Typography variant="body2" color="error">
                          {translate('header.verify_email')}
                          <Tooltip title={translate('header.send_new_verification_email')} arrow>
                            <Typography
                              component="span"
                              color="error"
                              sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 'medium', ml: 0.5 }}
                              onClick={resendEmail}
                            >
                              {translate('header.resend_email')}
                            </Typography>
                          </Tooltip>
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                )}

                {isOrgUser && isPortalAdmin && isVerified && (
                  <Tooltip
                    title={!isLargeScreen ? translate('header.feedback', { defaultValue: 'Feedback' }) : ''}
                    arrow
                    placement="bottom"
                  >
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      onClick={handleOpenFeedbackPopup}
                      disableElevation
                      sx={{
                        ml: 1,
                        fontWeight: 600,
                        background: 'linear-gradient(90deg, #607d8b 0%, #90a4ae 100%)',
                        color: '#fff',
                        boxShadow: '0 2px 8px 0 rgba(96, 125, 139, 0.15)',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #90a4ae 0%, #607d8b 100%)',
                          boxShadow: '0 4px 16px 0 rgba(96, 125, 139, 0.25)',
                        },
                        px: isLargeScreen ? 2.5 : 1.5,
                        py: 1,
                        minWidth: isLargeScreen ? 'auto' : 40,
                        display: 'flex',
                        alignItems: 'center',
                        gap: isLargeScreen ? 1 : 0,
                      }}
                    >
                      <HeadsetMicIcon sx={{ fontSize: '1.1rem' }} />
                      {isLargeScreen && (
                        <Typography variant="button" sx={{ fontSize: '15px', letterSpacing: 0.2 }}>
                          {translate('header.feedback', { defaultValue: 'Feedback' })}
                        </Typography>
                      )}
                    </Button>
                  </Tooltip>
                )}
                {inactiveProfiles.length > 0 && (
                  <Tooltip title={translate('header.inactive_connected_profiles', { defaultValue: 'Inactive connected profiles' })} arrow placement="bottom">
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      onClick={() => setInactiveDialogOpen(true)}
                      startIcon={<WarningAmberIcon sx={{ fontSize: '1rem' }} />}
                      sx={{
                        ml: 1,
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: isLargeScreen ? 1.5 : 1,
                        minWidth: isLargeScreen ? 'auto' : 40,
                      }}
                    >
                      {isLargeScreen ? `${inactiveProfiles.length} inactive` : inactiveProfiles.length}
                    </Button>
                  </Tooltip>
                )}
                {isShowStartTrialButton() && (
                  <Tooltip
                    title={!isLargeScreen ? translate('billing.subscription_info.trail_plan') : ''}
                    arrow
                    placement="bottom"
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setShowPlanDetailsPopup(true)}
                      sx={{
                        ml: 1,
                        fontWeight: 600,
                        background: 'linear-gradient(90deg, #e53935 0%, #ef5350 100%)',
                        color: '#fff',
                        boxShadow: '0 2px 8px 0 rgba(239, 83, 80, 0.25)',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #ef5350 0%, #e53935 100%)',
                          boxShadow: '0 4px 12px 0 rgba(239, 83, 80, 0.4)',
                        },
                        px: isLargeScreen ? 2 : 1.5,
                        py: 0.8,
                        minWidth: isLargeScreen ? 'auto' : 48,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: isLargeScreen ? 0.8 : 0.4,
                      }}
                    >
                      <CardGiftcardIcon sx={{ fontSize: '1.3rem', color: '#ffe5e5' }} />
                      {isLargeScreen && (
                        <Box sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              fontSize: '13px',
                              color: '#ffebee',
                              textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                            }}
                          >
                            {translate('billing.subscription_info.trail_plan')}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              fontSize: '11px',
                              color: '#ffe5e5',
                              opacity: 0.95,
                            }}
                          >
                            Exp: {CommonHelper.formatDateAndTime(currentPlanInfo?.expire_date, 'date')}
                          </Typography>
                        </Box>
                      )}
                    </Button>
                  </Tooltip>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.82rem',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    background: '#f5f7fa',
                    boxShadow: '0 1px 4px 0 rgba(33,150,243,0.04)',
                    minHeight: 32,
                    width: 'fit-content',
                    mx: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: theme.palette.primary.main, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#444', fontWeight: 500, px: 0.5, textAlign: 'center' }}>
                      {translate('header.current_timezone')} {CommonHelper.GetBrandTimeZone()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: 'pointer',
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                      mt: 0.5,
                      fontWeight: 500,
                      textAlign: 'center',
                      width: '100%',
                      '&:hover': {
                        textDecoration: 'none',
                        color: theme.palette.primary.dark,
                      },
                    }}
                    onClick={handleHelperTextClick}
                  >
                    {translate('header.want_to_change_it')}?
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                  <Select
                    value={language}
                    onChange={handleLanguageChange}
                    input={<InputBase />}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: '15px',
                      background: '#f5f7fa',
                      borderRadius: 1,
                      pl: 1,
                      pr: 2,
                      height: 32,
                      minWidth: 120,
                      boxShadow: '0 1px 4px 0 rgba(33,150,243,0.04)',
                    }}
                  >
                    <MenuItem value="english">
                      <span role="img" aria-label="English" style={{ marginRight: 8 }}>
                        🇬🇧
                      </span>{' '}
                      English
                    </MenuItem>
                    <MenuItem value="tamil">
                      <span role="img" aria-label="Tamil" style={{ marginRight: 8 }}>
                        🇮🇳
                      </span>{' '}
                      Tamil
                    </MenuItem>
                    <MenuItem value="german">
                      <span role="img" aria-label="German" style={{ marginRight: 8 }}>
                        🇩🇪
                      </span>{' '}
                      German{' '}
                      <span style={{ color: '#ff9800', fontWeight: 600, marginLeft: 6, fontSize: '12px' }}>(Beta)</span>
                    </MenuItem>
                    <MenuItem value="portuguese">
                      <span role="img" aria-label="Portuguese" style={{ marginRight: 8 }}>
                        🇵🇹
                      </span>{' '}
                      Portuguese{' '}
                      <span style={{ color: '#ff9800', fontWeight: 600, marginLeft: 6, fontSize: '12px' }}>(Beta)</span>
                    </MenuItem>
                    <MenuItem value="spanish">
                      <span role="img" aria-label="Spanish" style={{ marginRight: 8 }}>
                        🇪🇸
                      </span>{' '}
                      Spanish{' '}
                      <span style={{ color: '#ff9800', fontWeight: 600, marginLeft: 6, fontSize: '12px' }}>(Beta)</span>
                    </MenuItem>
                    <MenuItem value="french">
                      <span role="img" aria-label="French" style={{ marginRight: 8 }}>
                        🇫🇷
                      </span>{' '}
                      French
                      <span style={{ color: '#ff9800', fontWeight: 600, marginLeft: 6, fontSize: '12px' }}>(Beta)</span>
                    </MenuItem>
                    <MenuItem value="norwegian">
                      <span role="img" aria-label="Norwegian" style={{ marginRight: 8 }}>
                        🇳🇴
                      </span>{' '}
                      Norwegian{' '}
                      <span style={{ color: '#ff9800', fontWeight: 600, marginLeft: 6, fontSize: '12px' }}>(Beta)</span>
                    </MenuItem>
                    <MenuItem value="arabic">
                      <span role="img" aria-label="Arabic" style={{ marginRight: 8 }}>
                        🇸🇦
                      </span>
                      Arabic
                        <span style={{ color: '#ff9800', fontWeight: 600, marginLeft: 6, fontSize: '12px' }}>(Beta)</span>
                    </MenuItem>
                  </Select>
                </Box>
                <NotificationsPopover />
{/* 
                {!isCustomDomain && headerName && (
                  <IconButton onClick={handleOpenChatBot} sx={{ width: 40, height: 40 }}>
                    <HeadsetMicIcon sx={{ width: '20px', height: '20px' }} />
                  </IconButton>
                )} */}
                {headerName && (
                  <IconButton onClick={handleOpenSettings} sx={{ width: 40, height: 40 }}>
                    <SettingsIcon sx={{ width: '20px', height: '20px' }} />
                  </IconButton>
                )}
              </>
            )}
            <AccountPopover headerName={headerName} />
          </Stack>
        </StyledToolbar>
      </StyledRoot>
      {/* {showPlanDetailsPopup && <TrialPlanDetailsPopup closePopup={closePopup} />} */}
      {showRenewWarning && (
        <RenewPlanWarning
          open={showRenewWarning}
          currentPlanInfo={currentPlanInfo}
          onClose={() => setShowRenewWarning(false)}
          onRenew={() => {
            window.location.href = '/billing';
          }}
        />
      )}
      {showPreviousPaidPlanWarning && (
        <PreviousPaidPlanWarning
          open={showPreviousPaidPlanWarning}
          previousPaidPlan={previousPaidPlan}
          onClose={() => setShowPreviousPaidPlanWarning(false)}
        />
      )}
      <FeedbackPopup
        open={isFeedbackPopupOpen}
        onClose={handleCloseFeedbackPopup}
        onSubmit={handleFeedbackSubmit}
        loading={isFeedbackLoading}
        theme={theme}
      />
      <Dialog open={inactiveDialogOpen} onClose={() => setInactiveDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {translate('header.inactive_profiles', { defaultValue: 'Inactive Profiles' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Re-authenticate an account to restore chat access.
              </Typography>
            </Box>
            {inactiveProfilesLoading && <CircularProgress size={20} />}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {inactiveProfiles.map((profile, index) => {
              const serviceConfig = inactiveProfileServiceConfig[profile.service] || inactiveProfileServiceConfig.facebook;
              const ServiceIcon = serviceConfig.Icon;
              return (
                <Box key={`${profile.service}-${profile.profileID || index}`}>
                  <ListItem
                    secondaryAction={
                      <Button size="small" variant="outlined" onClick={() => handleInactiveProfileReauthenticate(profile)}>
                        Re-authenticate
                      </Button>
                    }
                    sx={{ pr: 18, py: 1.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar src={profile.picture_url || ''} sx={{ bgcolor: serviceConfig.color, color: '#fff' }}>
                        <ServiceIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={800}>{profile.displayName}</Typography>
                          <Chip size="small" label={profile.serviceLabel} variant="outlined" />
                          <Chip size="small" color="error" label="Inactive" />
                        </Stack>
                      }
                      secondary={profile.detail || profile.profileID || 'Connected profile'}
                    />
                  </ListItem>
                  {index < inactiveProfiles.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}
